import { Box, Switch, Text, VStack, Separator, Field } from '@chakra-ui/react';
import { AnalyticsFilterPanel } from './AnalyticsFilterPanel'
import type { FilterGroup, FilterFieldMetadata, TagOption } from '../../types/contactList'

interface ContactsPanelProps {
  showContacts: boolean
  onShowContactsChange: (v: boolean) => void
  useUniverse: boolean
  onUniverseChange: (v: boolean) => void
  /** Universe density routes through live-metrics — false on county/district geographies (would 400). */
  universeSupported: boolean
  hasActiveFilters: boolean
  filterGroups: FilterGroup[]
  onFilterGroupsChange: (groups: FilterGroup[]) => void
  fieldMetadata: FilterFieldMetadata[]
  tags: TagOption[]
  selectedListId: string | null
  onListIdChange: (listId: string | null) => void
}

export function ContactsPanel({
  showContacts,
  onShowContactsChange,
  useUniverse,
  onUniverseChange,
  universeSupported,
  hasActiveFilters,
  filterGroups,
  onFilterGroupsChange,
  fieldMetadata,
  tags,
  selectedListId,
  onListIdChange,
}: ContactsPanelProps) {
  // Loading a saved list into the map is an explicit "show me these people" action,
  // so auto-enable the contacts layer (off by default). Clearing the list leaves it as-is.
  const handleListIdChange = (listId: string | null) => {
    onListIdChange(listId)
    if (listId) onShowContactsChange(true)
  }

  return (
    <VStack gap={3} align="stretch" px={1} py={2}>
      <Field.Root>
        <Box display="flex" alignItems="center">
          <Switch.Root
            id="show-contacts-toggle"
            size="sm"
            checked={showContacts}
            onCheckedChange={e => onShowContactsChange(e.checked === true)}
            mr={2}
          >
            <Switch.HiddenInput />
            <Switch.Control>
              <Switch.Thumb />
            </Switch.Control>
          </Switch.Root>
          <Field.Label htmlFor="show-contacts-toggle" fontSize="xs" mb={0}>
            Show contacts on map
          </Field.Label>
        </Box>
        <Text fontSize="xs" color="gray.400" mt={1}>
          Off by default. When on, contacts cluster into numbered dots and split apart as you zoom in.
        </Text>
      </Field.Root>

      <Separator />

      <AnalyticsFilterPanel
        filterGroups={filterGroups}
        onFilterGroupsChange={onFilterGroupsChange}
        fieldMetadata={fieldMetadata}
        tags={tags}
        selectedListId={selectedListId}
        onListIdChange={handleListIdChange}
      />

      <Separator />

      <Field.Root>
        <Box display="flex" alignItems="center">
          <Switch.Root
            id="universe-toggle"
            size="sm"
            checked={useUniverse}
            disabled={!hasActiveFilters || !universeSupported}
            onCheckedChange={e => onUniverseChange(e.checked === true)}
            mr={2}
          >
            <Switch.HiddenInput />
            <Switch.Control>
              <Switch.Thumb />
            </Switch.Control>
          </Switch.Root>
          <Field.Label htmlFor="universe-toggle" fontSize="xs" mb={0}>
            Universe density per area
          </Field.Label>
        </Box>
        <Text fontSize="xs" color="gray.500" mt={1}>
          Paints each area by how many of your filtered contacts live there — an area view, not dots.
        </Text>
        {!universeSupported ? (
          <Text fontSize="xs" color="gray.400">
            Universe density needs precinct, tract, county or district geography — switch the Area geography to enable it.
          </Text>
        ) : !hasActiveFilters && (
          <Text fontSize="xs" color="gray.400">
            Add filters above to enable the universe layer.
          </Text>
        )}
      </Field.Root>
    </VStack>
  );
}
